import { useEffect, useMemo, useState } from 'react';
import {
  useActiveAccountId,
  useActiveProjectId,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import { type UnreviewedExtractedResource } from './useUnreviewedExtractedResources';
import {
  getCachedSourceReference,
  resolveExtractedResourceSourceReference,
} from './useExtractedResourceSource';

const itemKey = (item: { resourceType: string; id: string }) =>
  `${item.resourceType}/${item.id}`;

/**
 * Groups the needs-review extracted resources that share the same source
 * document as `selected`. Source references are resolved from each resource's
 * Provenance via the shared, cached resolver (the same cache used by the modal
 * source pane and the top-of-list preloader), so the set can shrink as items
 * are reviewed and reopening the modal does not re-fetch.
 *
 * When the selected resource has no resolvable source document, the group is
 * just the selected resource on its own.
 */
export const useSourceDocumentSiblings = (
  items: UnreviewedExtractedResource[],
  selected: UnreviewedExtractedResource | null,
): { siblings: UnreviewedExtractedResource[]; isResolving: boolean } => {
  const accountId = useActiveAccountId();
  const projectId = useActiveProjectId();
  const auth = useServiceAuth();

  const [resolvedVersion, setResolvedVersion] = useState(0);
  const [isResolving, setIsResolving] = useState(false);

  const itemsKey = items.map(itemKey).join(',');
  const enabled = !!selected && !!accountId && !!projectId && !!auth;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const deps = { auth, accountId, projectId };
    const unresolved = items.filter(
      item => getCachedSourceReference(projectId, item) === undefined,
    );
    if (unresolved.length === 0) {
      return;
    }

    let cancelled = false;
    setIsResolving(true);
    void Promise.all(
      unresolved.map(item =>
        resolveExtractedResourceSourceReference(deps, item),
      ),
    ).then(() => {
      if (!cancelled) {
        setIsResolving(false);
        setResolvedVersion(version => version + 1);
      }
    });

    return () => {
      cancelled = true;
    };
    // `resolvedVersion` intentionally excluded; the shared cache drives re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey, enabled, auth, accountId, projectId]);

  const siblings = useMemo(() => {
    if (!selected) {
      return [];
    }

    const selectedKey = itemKey(selected);
    const selectedSource = getCachedSourceReference(projectId, selected);

    if (!selectedSource) {
      // No shared source document resolved yet (or none exists): review the
      // selected resource on its own.
      return items.filter(item => itemKey(item) === selectedKey);
    }

    return items.filter(
      item => getCachedSourceReference(projectId, item) === selectedSource,
    );
    // `resolvedVersion` forces recomputation once provenance resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, selected, projectId, resolvedVersion]);

  return { siblings, isResolving };
};
